import type { ReactNode } from 'react';
import React, { useState } from 'react';
import type { TextButtonSize, TextSize, TextWeight } from '@wix/design-system';
import { Box, Text, TextButton } from '@wix/design-system';
import { ChevronRight, ChevronUp } from '@wix/wix-ui-icons-common/lazy';
import { useEssentials } from '../../contexts/essentials-context';
import classNames from 'classnames';
import { ExpandableTextDataHooks } from './data-hook';
import styles from './expandable-text.module.scss';

export interface ExpandableTextProps {
  children: ReactNode;
  onExpandToggleClick?: (isExpanded: boolean) => void;
  rows?: number;
  textSize?: TextSize;
  weight?: TextWeight;
  dataHook?: string;
  buttonSize?: TextButtonSize;
}

export const ExpandableText = ({
  children,
  onExpandToggleClick,
  rows = 10,
  textSize = 'medium',
  weight = 'thin',
  dataHook,
  buttonSize = 'medium',
}: ExpandableTextProps) => {
  const { t } = useEssentials();

  const [isTextCollapsed, setIsTextCollapsed] = useState(true);
  const [shouldShowExpandButton, setShouldShowExpandButton] = useState(false);

  const toggleTextExpanding = () => {
    setIsTextCollapsed((prevState) => !prevState);
    onExpandToggleClick?.(isTextCollapsed);
  };

  const saveEllipsisState = (value: boolean) => {
    // We want to save the text state. If it has ellipsis we want to show the read more/less button.
    // If it doesn't have ellipsis we don't want to show the button.
    setShouldShowExpandButton((prevState) => prevState || value);
  };

  return (
    <Box direction="vertical" dataHook={dataHook}>
      <Text
        ellipsis
        size={textSize}
        weight={weight}
        showTooltip={false}
        maxLines={isTextCollapsed ? rows : 1000}
        onEllipsisStateChange={saveEllipsisState}
        dataHook={ExpandableTextDataHooks.Text}
        className={classNames({
          [styles.descriptionText]: shouldShowExpandButton && isTextCollapsed,
        })}
      >
        {children}
      </Text>

      {shouldShowExpandButton && (
        <Box marginTop={isTextCollapsed ? '0px' : '16px'}>
          <TextButton
            dataHook={ExpandableTextDataHooks.Button}
            size={buttonSize}
            skin="dark"
            weight="thin"
            underline="always"
            suffixIcon={isTextCollapsed ? <ChevronRight /> : <ChevronUp />}
            onClick={toggleTextExpanding}
          >
            {isTextCollapsed
              ? t('app.page.overview.readMore')
              : t('app.page.overview.readLess')}
          </TextButton>
        </Box>
      )}
    </Box>
  );
};
